// src/services/healthMonitor.js - CORRECTED VERSION
import { logger } from '../utils/logger';
import { GCLOUD_CONFIG } from '../config';  

class HealthMonitor {
  constructor() {
    this.status = {
      ai: { 
        enabled: false, 
        configured: false,
        features_enabled: false,
        lastCheck: null, 
        checking: false,
        error: null,
        checked: false,
        connectionQuality: 'unknown',
        connectionQualityLabel: 'Unknown'
      },
      backend: { 
        healthy: false, 
        lastCheck: null,
        error: null,
        checked: false,
        connectionQuality: 'unknown',
        connectionQualityLabel: 'Unknown'
      },
      network: navigator.onLine
    };
    
    this.baseInterval = 30000;
    this.backoffMultiplier = 2;
    this.maxInterval = 300000;
    this.currentInterval = this.baseInterval;
    this.consecutiveFailures = 0;
    this.maxConsecutiveFailures = 3;
    
    this.debounceTimer = null;
    this.DEBOUNCE_MS = 2000;
    
    this.listeners = new Set();
    this.cid = 'health-monitor';
    
    // Add request controllers for cleanup
    this.currentAIController = null;
    this.currentBackendController = null;
    
    this.metrics = {
      backendLatency: null,
      aiLatency: null,
      lastSuccessfulCheck: null
    };
    
    logger.debug(this.cid, 'HealthMonitor initialized');
  }

  subscribe(callback) {
    logger.trace(this.cid, `Subscription added (total: ${this.listeners.size + 1})`);
    this.listeners.add(callback);
    
    // Immediately notify new subscriber with current status
    callback(this.status);
    
    return () => {
      logger.trace(this.cid, `Subscription removed (remaining: ${this.listeners.size})`);
      this.listeners.delete(callback);
    };
  }

  notify() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    this.debounceTimer = setTimeout(() => {
      logger.trace(this.cid, `Notifying ${this.listeners.size} listeners`, this.getSummary());
      this.listeners.forEach(callback => {
        try {
          callback(this.status);
        } catch (error) {
          logger.error(this.cid, 'Listener callback error', error);
        }
      });
    }, this.DEBOUNCE_MS);
  }

  getOverallStatus() {
    if (!this.status.network) return 'no-network';
    if (!this.status.backend.checked) return 'unknown';
    if (!this.status.backend.healthy) return 'backend-down';
    if (!this.status.ai.enabled) return 'ai-disabled';
    if (!this.status.ai.configured) return 'ai-misconfigured';
    return 'healthy';
  }

  getSummary() {
    return {
      overall: this.getOverallStatus(),
      timestamp: new Date(),
      network: this.status.network,
      backend: {
        healthy: this.status.backend.healthy,
        lastCheck: this.status.backend.lastCheck,
        latency: this.metrics.backendLatency
      },
      ai: {
        enabled: this.status.ai.enabled,
        configured: this.status.ai.configured,
        featuresEnabled: this.status.ai.features_enabled,
        lastCheck: this.status.ai.lastCheck,
        latency: this.metrics.aiLatency
      },
      consecutiveFailures: this.consecutiveFailures,
      checkInterval: this.currentInterval
    };
  }

  restartMonitoring(newInterval) {
    logger.debug(this.cid, `Restarting monitoring with ${newInterval}ms interval`);
    this.stopMonitoring();
    this.startMonitoring(newInterval);
  }

  async checkAI() {
    // Skip if already checking or tab is hidden
    if (this.status.ai.checking || document.hidden) {
      logger.trace(this.cid, 'Skipping AI check - already in progress or tab hidden');
      return;
    }
    
    logger.debug(this.cid, 'Starting AI health check');
    this.status.ai.checking = true;
    this.status.ai.error = null;
    
    // Abort any previous request
    if (this.currentAIController) {
      this.currentAIController.abort();
    }
    
    const controller = new AbortController();
    this.currentAIController = controller;
    
    const timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        logger.warn(this.cid, 'AI health check timeout (10s)');
        controller.abort();
      }
    }, 10000);
    
    const startTime = performance.now();
    
    try {
      const url = `${GCLOUD_CONFIG.appApiUrl}/copilot/health`;
      logger.debug(this.cid, 'Fetching AI health', { url });
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        credentials: 'same-origin'
      });
      
      clearTimeout(timeoutId);
      const requestTime = performance.now() - startTime;
      
      this.metrics.aiLatency = requestTime;
      
      if (requestTime > 1000) {
        logger.warn(this.cid, 'Slow AI health response', { latency: requestTime });
      }
      
      logger.trace(this.cid, 'AI health response', {
        status: response.status,
        timeMs: requestTime
      });
      
      const aiQuality = this.classifyConnectionQuality(requestTime);
      this.status.ai.connectionQuality = aiQuality;
      this.status.ai.connectionQualityLabel = this.getConnectionQualityLabel(aiQuality);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      logger.debug(this.cid, 'AI health data', {
        enabled: data.enabled,
        configured: data.configured,
        features_enabled: data.features_enabled
      });
      
      this.status.ai = { 
        enabled: data.enabled || false,
        configured: data.configured || false,
        features_enabled: data.features_enabled || false,
        model: data.model || null,
        lastCheck: new Date(),
        checking: false,
        error: null,
        details: data,
        checked: true
      };
      
      // Reset failure count on success
      this.consecutiveFailures = 0;
      this.metrics.lastSuccessfulCheck = new Date();
      
    } catch (error) {
      clearTimeout(timeoutId);
      const requestTime = performance.now() - startTime;
      this.metrics.aiLatency = requestTime;
      
      // Don't log AbortError as errors - they're expected
      if (error.name === 'AbortError') {
        logger.debug(this.cid, 'AI health check aborted', { reason: error.message });
      } else {
        logger.error(this.cid, 'AI health check failed', error);
      }
      
      const aiQuality = this.classifyConnectionQuality(requestTime);
      this.status.ai.connectionQuality = aiQuality;
      this.status.ai.connectionQualityLabel = this.getConnectionQualityLabel(aiQuality);
      
      this.status.ai = { 
        ...this.status.ai,
        lastCheck: new Date(),
        checking: false,
        error: error.name === 'AbortError' ? 'Request aborted' : error.message
      };
      
      // Only increment failures for non-abort errors
      if (error.name !== 'AbortError') {
        this.consecutiveFailures++;
        
        if (this.consecutiveFailures > this.maxConsecutiveFailures) {
          const newInterval = Math.min(
            this.baseInterval * Math.pow(this.backoffMultiplier, this.consecutiveFailures - this.maxConsecutiveFailures),
            this.maxInterval
          );
          
          if (newInterval !== this.currentInterval) {
            this.currentInterval = newInterval;
            logger.warn(this.cid, `Applying backoff: ${this.currentInterval}ms interval`, {
              consecutiveFailures: this.consecutiveFailures
            });
            this.restartMonitoring(this.currentInterval);
          }
        }
      }
    } finally {
      // Clear the controller reference
      this.currentAIController = null;
    }
    
    this.notify();
  }

  async checkBackend() {
    // Skip if already checking or tab is hidden
    if (document.hidden) {
      logger.trace(this.cid, 'Skipping backend check - tab hidden');
      return;
    }
    
    logger.debug(this.cid, 'Starting backend health check');
    
    // Abort any previous request
    if (this.currentBackendController) {
      this.currentBackendController.abort();
    }
    
    const controller = new AbortController();
    this.currentBackendController = controller;
    
    const timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        logger.warn(this.cid, 'Backend health check timeout (10s)');
        controller.abort();
      }
    }, 10000);
    
    const startTime = performance.now();
    
    try {
      const url = `${GCLOUD_CONFIG.appApiUrl}/health`;
      logger.debug(this.cid, 'Fetching backend health', { url });
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        credentials: 'same-origin'
      });
      
      clearTimeout(timeoutId);
      const requestTime = performance.now() - startTime;
      this.metrics.backendLatency = requestTime;
      
      // Track slow responses
      if (requestTime > 1000) {
        logger.warn(this.cid, 'Slow backend response', { latency: requestTime });
      }
      
      logger.trace(this.cid, 'Backend health response', {
        status: response.status,
        timeMs: requestTime
      });
      
      const backendQuality = this.classifyConnectionQuality(requestTime);
      this.status.backend.connectionQuality = backendQuality;
      this.status.backend.connectionQualityLabel = this.getConnectionQualityLabel(backendQuality);

      logger.debug(this.cid, 'Backend connection quality', {
        latency: requestTime,
        quality: backendQuality,
        label: this.status.backend.connectionQualityLabel
      });

      this.status.backend = {
        healthy: response.ok,
        lastCheck: new Date(),
        error: response.ok ? null : `HTTP ${response.status}`,
        checked: true
      };
      
      logger.debug(this.cid, 'Backend status updated', {
        healthy: this.status.backend.healthy,
        checked: this.status.backend.checked,
        time: this.status.backend.lastCheck
      });
      
      // Reset failure count on success
      this.consecutiveFailures = 0;
      this.metrics.lastSuccessfulCheck = new Date();
      
    } catch (error) {
      clearTimeout(timeoutId);
      const requestTime = performance.now() - startTime;
      this.metrics.backendLatency = requestTime;
      
      // Don't log AbortError as errors
      if (error.name === 'AbortError') {
        logger.debug(this.cid, 'Backend health check aborted', { reason: error.message });
      } else {
        logger.error(this.cid, 'Backend health check failed', error);
      }
      
      const backendQuality = this.classifyConnectionQuality(requestTime);
      this.status.backend.connectionQuality = backendQuality;
      this.status.backend.connectionQualityLabel = this.getConnectionQualityLabel(backendQuality);
      
      this.status.backend = { 
        healthy: false, 
        lastCheck: new Date(),
        error: error.name === 'AbortError' ? 'Request aborted' : error.message,
        checked: true
      };
      
      // Only increment failures for non-abort errors
      if (error.name !== 'AbortError') {
        this.consecutiveFailures++;
        
        if (this.consecutiveFailures > this.maxConsecutiveFailures) {
          const newInterval = Math.min(
            this.baseInterval * Math.pow(this.backoffMultiplier, this.consecutiveFailures - this.maxConsecutiveFailures),
            this.maxInterval
          );
          
          if (newInterval !== this.currentInterval) {
            this.currentInterval = newInterval;
            logger.warn(this.cid, `Applying backoff: ${this.currentInterval}ms interval`, {
              consecutiveFailures: this.consecutiveFailures
            });
            this.restartMonitoring(this.currentInterval);
          }
        }
      }
    } finally {
      // Clear the controller reference
      this.currentBackendController = null;
    }
    
    this.notify();
  }

  // Network event handlers as class methods
  handleNetworkOnline = () => {
    logger.info(this.cid, 'Network online');
    this.status.network = true;
    // Do immediate check when network returns
    this.checkBackend();
    this.checkAI();
    this.notify();
  };
  
  handleNetworkOffline = () => {
    logger.warn(this.cid, 'Network offline');
    this.status.network = false;
    this.notify();
  };

  // Page visibility handler
  handleVisibilityChange = () => {
    if (document.hidden) {
      logger.debug(this.cid, 'Tab hidden, pausing health checks');
      this.stopMonitoring();
    } else {
      logger.debug(this.cid, 'Tab visible, resuming health checks');
      // Do immediate check when returning to tab
      this.checkBackend();
      this.checkAI();
      this.startMonitoring(this.currentInterval);
    }
  };

  startMonitoring(interval = 30000) {
    // Reset to base interval if explicitly called
    if (interval === 30000) {
      this.currentInterval = this.baseInterval;
      this.consecutiveFailures = 0;
    }
    
    logger.info(this.cid, `Starting health monitoring (interval: ${this.currentInterval}ms)`);
    
    // Initial checks
    this.checkBackend();
    this.checkAI();
    
    // Clear any existing interval
    if (this.interval) {
      clearInterval(this.interval);
    }
    
    // Periodic checks
    this.interval = setInterval(() => {
      logger.trace(this.cid, 'Scheduled health check cycle');
      this.checkBackend();
      this.checkAI();
    }, this.currentInterval);

    // Network monitoring with bound methods
    window.addEventListener('online', this.handleNetworkOnline);
    window.addEventListener('offline', this.handleNetworkOffline);
    
    // Page visibility monitoring
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  stopMonitoring() {
    logger.info(this.cid, 'Stopping health monitoring');
    
    // Abort any ongoing requests
    if (this.currentAIController && !this.currentAIController.signal.aborted) {
      this.currentAIController.abort();
    }
    if (this.currentBackendController && !this.currentBackendController.signal.aborted) {
      this.currentBackendController.abort();
    }
    
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleNetworkOnline);
    window.removeEventListener('offline', this.handleNetworkOffline);
  }

  classifyConnectionQuality(latencyMs) {
    if (latencyMs === null || latencyMs === undefined) {
      return 'unknown';
    }
    
    if (latencyMs > 10000) {
      return 'timeout';
    }
    
    if (latencyMs > 3000) {
      return 'poor';
    }
    
    if (latencyMs > 1000) {
      return 'fair';
    }
    
    if (latencyMs > 300) {
      return 'good';
    }
    
    return 'excellent';
  }

  getConnectionQualityLabel(quality) {
    const labels = {
      'excellent': 'Excellent (<300ms)',
      'good': 'Good (300-1000ms)',
      'fair': 'Fair (1-3s)',
      'poor': 'Poor (>3s)',
      'timeout': 'Timeout (>10s)',
      'unknown': 'Unknown'
    };
    return labels[quality] || 'Unknown';
  }

  destroy() {
    this.stopMonitoring();
    this.listeners.clear();
    logger.debug(this.cid, 'HealthMonitor destroyed');
  }
}

export const healthMonitor = new HealthMonitor();